import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl, formatFileSize } from "@/lib/format";
import { useDocumentTitle } from "@/lib/use-document-title";
import { ScrollReveal } from "@/components/scroll-reveal";
import type {
  Project,
  ProjectUnit,
  TechnicalPdf,
  TechnicalSpec,
} from "@shared/schema";

type Detail = { project: Project; units: ProjectUnit[] };

/**
 * Részletes műszaki tartalom oldal — a projekt admin felületén ("Műszaki
 * tartalom" szekció) feltöltött tartalmat jeleníti meg: a strukturált
 * ikon+szöveg adatsorokat és a feltöltött műszaki PDF dokumentumot.
 * Ugyanabból a forrásból dolgozik, mint a projekt-oldal Műszaki tartalom
 * blokkja (project.technicalSpecs + project.technicalPdf).
 */
export function UnitSpecsPage({ slug }: { slug: string }) {
  const q = useQuery({
    queryKey: ["project", slug],
    queryFn: () => api<Detail>(`/api/projects/${slug}`, { withAuth: false }),
  });

  useDocumentTitle(
    q.data ? `Műszaki tartalom · ${q.data.project.name}` : "Műszaki tartalom",
  );

  if (q.isLoading) {
    return (
      <div className="container py-24 text-brand-500">Betöltés...</div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="container py-24 text-center">
        <h1 className="section-title text-3xl">A projekt nem található</h1>
        <Link href="/projektek" className="btn-primary mt-6 inline-flex">
          ← Vissza a projektekhez
        </Link>
      </div>
    );
  }

  const { project } = q.data;
  const pdf = project.technicalPdf;
  const specs = [...project.technicalSpecs].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const isEmpty = !pdf && specs.length === 0;

  return (
    <div className="bg-white">
      {/* Header band */}
      <section className="border-b border-brand-100 bg-brand-50/50">
        <div className="container py-8">
          <Link
            href={`/projektek/${project.slug}`}
            // relative z-40: az átlátszó fix fejléc e sáv fölé lóg és elnyelné
            // a kattintást (ugyanaz, mint a unit-detail visszagombjánál, #19).
            className="relative z-40 inline-flex items-center gap-1.5 text-xs uppercase tracking-widish font-semibold text-brand-500 hover:text-accent transition-colors"
          >
            <ArrowLeft size={14} />
            <span>{project.name}</span>
          </Link>
          <div className="mt-4">
            <span className="section-eyebrow">Részletes adatok</span>
            <h1 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-heading font-normal uppercase tracking-ultrawide text-brand-700">
              Műszaki <span className="text-accent">tartalom</span>
            </h1>
            <p className="mt-3 text-sm text-brand-500 max-w-2xl">
              A projekt részletes műszaki tartalma — szerkezet, gépészet,
              nyílászárók, burkolatok és minden fontos műszaki paraméter,
              letölthető műszaki dokumentációval.
            </p>
          </div>
        </div>
      </section>

      {/* Technical content — the same admin-managed source as the project
          page's "Műszaki tartalom" block (structured spec rows + PDF). */}
      <section className="py-12 lg:py-16">
        <div className="container">
          {isEmpty ? (
            <ScrollReveal>
              <div className="card p-10 text-center max-w-2xl mx-auto">
                <FileText
                  size={40}
                  strokeWidth={1}
                  className="mx-auto text-brand-300"
                />
                <p className="mt-4 text-sm text-brand-500">
                  Ehhez a projekthez még nem került feltöltésre részletes
                  műszaki tartalom.
                </p>
                <Link
                  href={`/projektek/${project.slug}`}
                  className="btn-primary mt-6 inline-flex"
                >
                  <ArrowLeft size={14} />
                  Vissza a projekthez
                </Link>
              </div>
            </ScrollReveal>
          ) : (
            <>
              {specs.length > 0 && (
                <ScrollReveal>
                  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-10">
                    {specs.map((spec: TechnicalSpec, i: number) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-brand-700"
                      >
                        <span
                          className="text-xl leading-6 shrink-0 w-7 text-center"
                          aria-hidden="true"
                        >
                          {spec.icon || "•"}
                        </span>
                        <span className="text-sm leading-6 pt-0.5">
                          {spec.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </ScrollReveal>
              )}

              {pdf && <TechnicalPdfBlock pdf={pdf} />}
            </>
          )}
        </div>

        {/* Action footer */}
        {!isEmpty && (
          <div className="container mt-10 flex flex-wrap items-center justify-between gap-4">
            <Link
              href={`/projektek/${project.slug}`}
              className="btn-secondary"
            >
              <ArrowLeft size={14} />
              Vissza a projekthez
            </Link>
            <Link
              href={`/projektek/${project.slug}/osszehasonlitas`}
              className="btn-primary"
            >
              Lakások összehasonlítása
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

/** Download card + inline preview for the uploaded technical PDF. */
function TechnicalPdfBlock({ pdf }: { pdf: TechnicalPdf }) {
  const url = fileUrl(pdf.key);
  const sizeLabel = formatFileSize(pdf.size);

  return (
    <ScrollReveal>
      <div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="card p-4 flex items-center gap-3 hover:bg-brand-50 transition-colors group max-w-md"
        >
          <FileText size={24} className="text-accent shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-medium text-brand-700 group-hover:text-accent transition-colors truncate">
              {pdf.name || "Műszaki dokumentum"}
            </div>
            <div className="text-xs text-brand-400">
              PDF dokumentum{sizeLabel ? ` · ${sizeLabel}` : ""}
            </div>
          </div>
          <Download
            size={18}
            className="text-brand-400 group-hover:text-accent shrink-0"
          />
        </a>

        {/* Inline PDF preview; a letöltés-kártya marad az elsődleges elérés,
            ha a böngésző nem tudja beágyazva megjeleníteni. */}
        <div className="mt-6 border border-brand-100">
          <iframe
            src={url}
            title={pdf.name || "Műszaki dokumentum"}
            className="w-full h-[70vh] min-h-[420px]"
          />
        </div>
      </div>
    </ScrollReveal>
  );
}
